/**
 * Studio Admin → Token health. Per integration, how many connected accounts are
 * healthy, stale, failing or need reconnecting, and who has to reconnect so
 * someone can let them know.
 *
 * The API sends names, counts, times and an error class, never a token; this
 * page shows exactly those fields and nothing else from a row.
 */
import { ChevronRight, RotateCw } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { adminClient, type TokenHealthResponse, type TokenHealthState } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { LoadError, Skeleton, SkeletonList } from '@/components/ui/load-state';
import { formatCount } from '@/lib/format';
import type { StatusTone } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

import { formatAbsolute, formatRelative, formatUpdatedAt, needsAttention, sortByAttention } from './tokenHealthModel';

type StudioRow = TokenHealthResponse['studio']['integrations'][number];
type StudioAttention = StudioRow['attention'][number];

const STATES: Array<{ state: TokenHealthState; label: string; tone: StatusTone }> = [
  { state: 'healthy', label: 'Healthy', tone: 'success' },
  { state: 'stale', label: 'Stale', tone: 'warning' },
  { state: 'failing', label: 'Failing', tone: 'danger' },
  { state: 'needs_reconnect', label: 'Needs reconnect', tone: 'danger' },
];

const STATE_LABEL: Record<'failing' | 'needs_reconnect', string> = { failing: 'Failing', needs_reconnect: 'Needs reconnect' };

const NO_ACCOUNTS = 'No connected accounts yet';

function CountBadges({ counts }: { counts: Record<TokenHealthState, number> }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {STATES.map(({ state, label, tone }) => {
        const count = counts[state] ?? 0;
        return (
          <span key={state} data-muted={count === 0 ? 'true' : 'false'} className={cn(count === 0 && 'opacity-60')}>
            <StatusPill tone={count > 0 ? tone : 'neutral'}>
              {label}
              <span className="font-mono tabular-nums">{formatCount(count)}</span>
            </StatusPill>
          </span>
        );
      })}
    </div>
  );
}

function StatePill({ state }: { state: 'failing' | 'needs_reconnect' }) {
  return <StatusPill tone="danger">{STATE_LABEL[state]}</StatusPill>;
}

function Person({ name, email, fallback }: { name?: string; email?: string; fallback: string }) {
  if (!name && !email) return <span className="text-muted-foreground">{fallback}</span>;
  return (
    <div className="min-w-0">
      <div className="truncate font-medium">{name ?? email}</div>
      {name && email ? <div className="truncate text-xs text-muted-foreground">{email}</div> : null}
    </div>
  );
}

function When({ iso, now }: { iso?: string; now: number }) {
  return (
    <span title={formatAbsolute(iso)} className="whitespace-nowrap">
      {formatRelative(iso, now)}
    </span>
  );
}

function ErrorClass({ value }: { value?: string }) {
  return value ? <code className="font-mono text-xs">{value}</code> : <span className="text-muted-foreground">None</span>;
}

function whoFor(row: StudioAttention): ReactNode {
  if (row.scope === 'app') return <Person name={row.projectName} fallback="An app's own account" />;
  if (row.scope === 'org') return <span className="text-muted-foreground">Organization account</span>;
  return <Person name={row.personName} email={row.personEmail} fallback="Unknown person" />;
}

function StudioAttentionTable({ rows, omitted, now }: { rows: StudioAttention[]; omitted: number; now: number }) {
  return (
    <div className="px-5 pb-4">
      <DataTable>
        <DataTableHead
          columns={[{ label: 'Person' }, { label: 'Organization' }, { label: 'Account' }, { label: 'State' }, { label: 'Last refreshed' }, { label: 'Error' }]}
        />
        <tbody>
          {rows.map((row, index) => (
            <DataTableRow key={index}>
              <DataTableCell>{whoFor(row)}</DataTableCell>
              <DataTableCell>{row.organizationName ?? row.organizationId}</DataTableCell>
              <DataTableCell className="text-muted-foreground">{row.accountLabel ?? '—'}</DataTableCell>
              <DataTableCell>
                <StatePill state={row.state} />
              </DataTableCell>
              <DataTableCell>
                <When iso={row.lastRefreshedAt} now={now} />
              </DataTableCell>
              <DataTableCell>
                <ErrorClass value={row.lastRefreshError} />
              </DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      </DataTable>
      {omitted > 0 ? <p className="mt-2 text-xs text-muted-foreground">+{formatCount(omitted)} more</p> : null}
    </div>
  );
}

/** One integration or module: its name and counts, and when anything needs attention, the table behind a disclosure that starts open. */
function HealthRow({ title, counts, children }: { title: string; counts: Record<TokenHealthState, number>; children?: ReactNode }) {
  const header = (chevron: boolean) => (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3">
      <div className="flex min-w-0 items-center gap-2">
        {chevron ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
        ) : (
          <span className="w-4 shrink-0" aria-hidden="true" />
        )}
        <span className="truncate text-sm font-medium">{title}</span>
      </div>
      <CountBadges counts={counts} />
    </div>
  );
  if (!children) return <div>{header(false)}</div>;
  return (
    <details open className="group">
      <summary className="cursor-pointer list-none hover:bg-muted/40 [&::-webkit-details-marker]:hidden">{header(true)}</summary>
      {children}
    </details>
  );
}

function StudioSection({ integrations, now }: { integrations: StudioRow[]; now: number }) {
  return (
    <AdminSection title="Studio" bodyClassName="p-0">
      {integrations.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground">{NO_ACCOUNTS}</p>
      ) : (
        <div className="divide-y divide-border">
          {sortByAttention(integrations).map((item) => (
            <HealthRow key={item.integrationId} title={item.title} counts={item.counts}>
              {needsAttention(item) ? <StudioAttentionTable rows={item.attention} omitted={item.attentionOmitted} now={now} /> : null}
            </HealthRow>
          ))}
        </div>
      )}
    </AdminSection>
  );
}

const INTRO = (days: number) => `Connected accounts are refreshed every ${days} days. Accounts that need reconnecting are listed so you can let the person know.`;

/** The report itself, with the data already in hand. */
export function TokenHealthView({ data, now = Date.now(), onRefresh, refreshing = false }: { data: TokenHealthResponse; now?: number; onRefresh?: () => void; refreshing?: boolean }) {
  const updated = formatUpdatedAt(data.generatedAt);
  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Token health"
        description={INTRO(data.refreshEveryDays)}
        actions={
          <div className="flex items-center gap-3">
            {updated ? <span className="text-xs text-muted-foreground">Updated {updated}</span> : null}
            {onRefresh ? (
              <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
                <RotateCw className={cn('mr-1.5 h-3.5 w-3.5', refreshing && 'animate-spin')} aria-hidden="true" />
                Refresh
              </Button>
            ) : null}
          </div>
        }
      />
      <StudioSection integrations={data.studio.integrations} now={now} />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="Token health" description={INTRO(7)} />
      <AdminSection title="Studio" bodyClassName="p-0">
        <SkeletonList
          rows={3}
          label="Loading token health"
          className="divide-y divide-border"
          rowClassName="flex items-center justify-between gap-4 px-5 py-3.5"
          renderRow={() => (
            <>
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-72" />
            </>
          )}
        />
      </AdminSection>
    </div>
  );
}

export function TokenHealthPage() {
  const [data, setData] = useState<TokenHealthResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      setData(await adminClient.getTokenHealth());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Failed to load token health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data && loading) return <LoadingSkeleton />;
  if (!data) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <AdminPageHeader title="Token health" description={INTRO(7)} />
        <LoadError message={loadError} onRetry={() => void load()} className="rounded-xl border border-border bg-card" />
      </div>
    );
  }
  return <TokenHealthView data={data} onRefresh={() => void load()} refreshing={loading} />;
}
